<template>
  <settings-form :breadcrumbs="breadcrumbs">
    <template #content>
      <!-- Grid -->
      <v-row class="pa-0">
        <v-col
          :class="['d-none d-sm-block',{'calendar-col-compact': $vuetify.breakpoint.smAndDown}]"
          cols="3"
        >
          <!--  -->
        </v-col>
        <v-col>
          <div class="d-flex">
            <!-- Date Title -->
            <v-btn
              color="primary"
              icon
              @click="prevDay"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <div
              class="text-subtitle-1 text-sm-h6 flex-grow-1 text-center"
              style="margin: auto"
            >
              {{ $localized.fullDateText(date) || '' }}
            </div>
            <v-btn
              color="primary"
              icon
              @click="nextDay"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </div>
        </v-col>
        <v-col
          :class="['text-right',{'calendar-col-compact': $vuetify.breakpoint.smAndDown}]"
          cols="3"
        >
          <v-menu
            v-model="timeMenu"
            offset-y
            :close-on-content-click="false"
          >
            <template #activator="{ on }">
              <v-btn
                class="mr-0"
                color="primary"
                outlined
                v-on="on"
              >
                <v-icon>mdi-calendar-month</v-icon>
                <span class="pl-1 d-none d-md-block">{{ $t('button.select_date') }}</span>
              </v-btn>
            </template>
            <v-container class="pa-0">
              <v-date-picker
                :value="dateString"
                :first-day-of-week="$localized.firstDayOfWeek()"
                class="date-picker-menu"
                no-title
                color="brand"
                scrollable
                @input="updateDate"
              />
            </v-container>
          </v-menu>
        </v-col>
      </v-row>

      <!-- Buttons -->
      <v-row class="pt-2 d-flex justify-center">
        <v-btn
          :block="$vuetify.breakpoint.xsOnly"
          color="error"
          class="ma-2"
          @click="closeConfirmation = true"
        >
          <v-icon>
            mdi-alarm-off
          </v-icon>
          {{ $t('button.close_all_slots') }}
        </v-btn>
        <v-btn
          :block="$vuetify.breakpoint.xsOnly"
          color="primary"
          class="ma-2"
          @click="resetConfirmation = true"
        >
          <v-icon>
            mdi-alarm
          </v-icon>
          {{ $t('button.reset_to_default') }}
        </v-btn>
      </v-row>
      <v-row>
        <!-- /// -->
        <!-- TimeSlot Grid -->
        <!-- TODO: enable reset when 'delete' is supported by backend, now false -->
        <hour-slots-grid
          :hour-slots="hourSlots"
          :profiles="profiles"
          :enable-reset="false"
          @profile-selected="profileSelected"
        />
      <!-- /// -->
      </v-row>
    </template>
    <!-- Close All Slots Confirmation Dialog -->
    <confirmation-dialog
      :visible="closeConfirmation"
      :message="$t('message.confirm_disable_all_day')"
      @confirm="closeSlots"
      @close="closeConfirmation = false"
    />

    <!-- Reset All Slots Confirmation Dialog -->
    <confirmation-dialog
      :visible="resetConfirmation"
      :message="$t('message.confirm_reset_all_day')"
      @confirm="resetSlots"
      @close="resetConfirmation = false"
    />
  </settings-form>
</template>

<script lang="ts">
import Vue from 'vue';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import SettingsBreadcrumbs from '@/mixins/settings-breadcrumbs';
import {
  dateByAddingDays, dateTimeIndexFromDateAndTimeIndex, dateIndexFromDate,
  MORNING_TIME_SLOTS_COUNT, DAY_TIME_SLOTS_COUNT,
  dateStringFromDate8601, dateFromDateString8601, timeIndexFromSlot, weekdayNumberFromDateDay,
} from '@/services/time-utils';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import { performSaveAction } from '@/services/vue-utils';
import OpeningHour from '@/model/OpeningHour';
import DateAndTime from '@/model/DateAndTime';
import TimeSlot from '@/model/TimeSlot';
import HourSlotProfile, { profilesFromHours } from '@/model/HourSlotProfile';
import HourSlotsGrid, { HourSlotMenuItemType } from '@/components/views/settings/HourSlotsGrid.vue';
import HourSlot from '@/model/HourSlot';

export default Vue.extend({
  name: 'SpecialHours',
  components: {
    ConfirmationDialog, SettingsForm, HourSlotsGrid,
  },
  mixins: [SettingsBreadcrumbs],
  data() {
    return {
      date: new Date(),
      timeMenu: false,
      closeConfirmation: false,
      resetConfirmation: false,
      breadcrumbs: [],
      unwatch: Function as (() => void),
    };
  },
  computed: {
    dateString(): string {
      return dateStringFromDate8601(this.date)!;
    },
    hourSlots(): (TimeSlot | null)[] {
      const ohmap = new Map<number, OpeningHour>();
      const weekdayNumber = weekdayNumberFromDateDay(this.date.getDay());
      const openingHours = this.$tstore.state.settings.openingHours as OpeningHour[];
      openingHours.forEach((o) => { if (o.weekdayNumber === weekdayNumber) ohmap.set(o.timeIndex, o); });

      const shmap = new Map<number, DateAndTime>();
      const dateIndex = dateIndexFromDate(this.date);
      const dateSlot = this.$tstore.state.reservations.dateSlotsByDateIndex[dateIndex];
      (dateSlot?.datesAndTimes ?? []).forEach((o) => { shmap.set(o.timeIndex, o); });

      const slots: (TimeSlot|null)[] = [];
      for (let si = MORNING_TIME_SLOTS_COUNT; si < DAY_TIME_SLOTS_COUNT; si += 1) {
        const ti = timeIndexFromSlot(si);
        const oh = ohmap.get(ti) ?? undefined;
        const sh = shmap.get(ti) ?? undefined;
        const dti = dateTimeIndexFromDateAndTimeIndex(dateIndex, ti);

        const ts = oh || sh ? new TimeSlot(si, dti, sh, oh) : null;

        slots.push(ts);
      }

      return slots;
    },
    profilesById(): Map<number, HourSlotProfile> {
      const openingHours = this.$tstore.state.settings.openingHours as OpeningHour[];
      const datesAndTimes = this.$tstore.getters.datesAndTimes as DateAndTime[];
      return profilesFromHours([...openingHours, ...datesAndTimes]);
    },
    profiles(): HourSlotProfile[] {
      return Array.from(this.profilesById.values());
    },
  },
  beforeDestroy() {
    this.unwatch();
  },
  methods: {
    async profileSelected(hourIndex: number, type: number, IDOrHourSlot: number | HourSlot) {
      const slotIndex = hourIndex + MORNING_TIME_SLOTS_COUNT;
      const dateAndTime = DateAndTime.fromDateAndSlot(this.date, slotIndex);
      dateAndTime.isOpen = true;
      let profile: HourSlotProfile | undefined;

      let isDeleted = false;

      switch (type) {
        case HourSlotMenuItemType.CLOSED:
        // closed - closed special hour
          dateAndTime.isOpen = false;
          break;
        case HourSlotMenuItemType.OPEN:
        // just open - use empty special hour
          break;
        case HourSlotMenuItemType.CUSTOM:
        // existing custom - fill and use special hour from existing profile
          profile = this.profilesById.get(IDOrHourSlot as number);
          dateAndTime.setParams(profile ?? {});
          break;
        case HourSlotMenuItemType.CUSTOM_NEW:
        // new custom - fill and use special hour from values
          dateAndTime.setParams(IDOrHourSlot as HourSlot);
          break;
        case HourSlotMenuItemType.RESET:
        // reset - remove special hour and return !
          isDeleted = true;
          break;
        default:
          break;
      }

      // save hour
      const ok = await performSaveAction(
        undefined,
        async () => this.$tstore.dispatch('sendDateAndTime', { entity: dateAndTime, isDeleted }),
      );
    },
    updateDate(value: string) {
      this.timeMenu = false;
      this.date = dateFromDateString8601(value)!;
    },
    prevDay() {
      this.date = dateByAddingDays(new Date(this.date), -1);
    },
    nextDay() {
      this.date = dateByAddingDays(new Date(this.date), 1);
    },
    closeSlots() {
      this.closeOrResetSlots(true);
    },
    resetSlots() {
      this.closeOrResetSlots(false);
    },
    async closeOrResetSlots(close: boolean) {
      const di = dateIndexFromDate(this.date);
      const dti = dateTimeIndexFromDateAndTimeIndex(di, 0);

      const dt = new DateAndTime();
      dt.id = dti;
      dt.isOpen = !close;

      const ok = await performSaveAction(
        undefined,
        async () => this.$tstore.dispatch('sendDateAndTime', { entity: dt }),
      );
    },
  },
});

</script>

<style scoped>
  .calendar-col-compact {
  max-width: 86px;
  }
</style>
